import React, { useState } from "react";
import {
  Autocomplete,
  AutocompleteInput,
  AutocompleteItem,
  AutocompleteList,
  AutocompletePopup,
  cn,
  Text,
  useIsMobile,
} from "@salestrak/ui";
import {
  AddressPropertyInput,
} from "#app/components/issue-view/address-property-input";
import {
  PROPERTY_VALUE_INPUT_INSET_CLASS,
} from "#app/components/fields/property-value-inset";
import {
  useAddressAutocomplete,
} from "#app/lib/hooks/use-address-autocomplete";
import type { AddressSuggestion } from "#app/lib/hooks/address-search-types";
import {
  NONE_OPTION_LABEL,
  type FieldControlSize,
} from "#app/lib/custom-field-utils";
import type { Omittable } from "@salestrak/ui";
import { toControlSize } from "./field-control";

export function AddressInlineInput({
  wholeAddress,
  placeholder,
  inset = "cell",
  size,
  onSelect,
  onDeactivate,
  title,
}: Omittable<{
  wholeAddress?: string | null;
  /** The empty prompt the rest display shows, mirrored while editing. */
  placeholder?: string;
  /** `cell`: the host cell pads, the input none. `property`: the input
   *  carries the property value inset so its text sits where the rest
   *  display's did. */
  inset?: "cell" | "property";
  /** Search input rung. Omitted → the control's default (30px, the row
   *  cells); the board card's 26px token interior takes `sm`. */
  size?: FieldControlSize;
  onSelect: (wholeAddress: string) => void;
  onDeactivate: () => void;
  /** The phone sheet's caption — the field's name. See `AddressPropertyInput`. */
  title?: string;
}>) {
  const [query, setQuery] = useState("");
  // Escape/outside-press closed the suggestion popup; stay closed until the
  // query changes again (open is otherwise derived, so without this the
  // still-matching suggestions would reopen it on the next render).
  const [dismissed, setDismissed] = useState(false);
  const { suggestions } = useAddressAutocomplete(query);
  const isMobile = useIsMobile();

  if (isMobile) {
    return (
      <AddressPropertyInput
        variant="plain"
        wholeAddress={wholeAddress}
        onSelect={onSelect}
        title={title}
        autoOpen
        onOpenChange={(open) => {
          if (!open) onDeactivate();
        }}
      />
    );
  }

  return (
    <Autocomplete
      items={suggestions}
      // The Places proxy already filtered against the query — "none" keeps
      // Base UI from re-filtering (and from rewriting the input on highlight).
      mode="none"
      // First suggestion pre-highlighted, so Enter commits it (parity with
      // the popover flow this replaces).
      autoHighlight
      itemToStringValue={(s: AddressSuggestion) => s.description}
      value={query}
      onValueChange={(next, details) => {
        if (details.reason === "item-press") {
          onSelect(next);
          onDeactivate();
          return;
        }
        setDismissed(false);
        setQuery(next);
      }}
      open={!dismissed && suggestions.length > 0}
      onOpenChange={(open) => {
        if (!open) setDismissed(true);
      }}
    >
      <AutocompleteInput
        variant="table"
        className={cn(inset === "property" && PROPERTY_VALUE_INPUT_INSET_CLASS)}
        // Mirrors the rest display exactly: the current address while one is
        // set, the host's empty prompt (or the empty marker) while not.
        placeholder={wholeAddress || placeholder || NONE_OPTION_LABEL}
        size={toControlSize(size)}
        aria-label="Search for address"
        autoFocus
        onKeyDown={(event: React.KeyboardEvent) => {
          if (event.key === "Escape") onDeactivate();
        }}
      />
      <AutocompletePopup>
        <AutocompleteList aria-label="Address suggestions">
          {(s: AddressSuggestion) => (
            <AutocompleteItem key={s.placeId} value={s}>
              {/* Wraps: a clipped address drops the unit or the city, which
                  is what distinguishes two otherwise identical suggestions. */}
              <Text size="sm">{s.description}</Text>
            </AutocompleteItem>
          )}
        </AutocompleteList>
      </AutocompletePopup>
    </Autocomplete>
  );
}
